import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { validate, type AuthSessionResponse, type MachineListItem } from '@brewcore/app-contract';
import {
  AuthClient,
  BrewcoreClient,
  calmNet,
  hashOfPage,
  PairingApi,
  pageOfHash,
  pairingTokenFromLocation,
  parentOf,
  reasonText,
  t,
  tabOfHash,
  type BrewPage,
  type CarePage,
  type Lang,
  type NetState,
  type SettingsPage,
  type SocketLike,
  type SubPage,
} from '../core/index.js';
import { MachineHome } from './MachineHome.js';
import { LoginScreen } from './LoginScreen.js';
import { AccountSheet } from './AccountSheet.js';
import { PairingFlow } from './PairingFlow.js';
import { FirstRun } from './FirstRun.js';
import { tabItems, type TabId } from './tabs.js';
import { navMotionOf, navigate, type NavMotion } from './nav-motion.js';
import { MachineProvider, useMachine, useNow } from './machine-context.js';
import { DocumentTitle } from './device/DocumentTitle.js';
import { BrewTab } from './tabs/BrewTab.js';
import { SettingsTab } from './tabs/SettingsTab.js';
import { CareTab } from './tabs/CareTab.js';
import { CommandToast } from './tabs/parts.js';
import { AccountButton, BrandMark, Icon, Spinner, TabBar } from './components/index.js';
// Claude support (src/ai, feature/claude-assist)
import { forgetClaudeOnDevice } from '../ai/storage.js';
import { ClaudeSettingsLink } from './ai/use-assistant.js';

export const APP_VERSION = '0.1.0';

/** what the app keeps in a history entry: pushed by the app (the way back is history.back()), its place n */
interface NavState {
  brewcore?: boolean;
  n?: number;
}

const PAIR_TOKEN_KEY = 'brewcore.pairToken';

/**
 * Token of a QR link (/p#token). It is taken out of the address bar at once (AD-42: no token in the history); a
 * visitor who still has to sign in keeps it for this tab only, the dialog opens after the login.
 */
function takePairingToken(): string | null {
  const fromUrl = pairingTokenFromLocation(location);
  if (fromUrl) {
    history.replaceState(null, '', '/');
    try {
      sessionStorage.setItem(PAIR_TOKEN_KEY, fromUrl);
    } catch {
      // storage blocked: the dialog still opens for this page view
    }
    return fromUrl;
  }
  try {
    return sessionStorage.getItem(PAIR_TOKEN_KEY);
  } catch {
    return null;
  }
}

function forgetPairingToken(): void {
  try {
    sessionStorage.removeItem(PAIR_TOKEN_KEY);
  } catch {
    // nothing kept
  }
}

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem('brewcore.lang');
    if (saved === 'de' || saved === 'en') return saved;
  } catch {
    // storage may be blocked; German is the default
  }
  return 'de';
}

/** The Google callback returns to /login?error=<code> on failure; read it once and clean the address. */
function takeLoginError(lang: Lang): string | null {
  if (location.pathname !== '/login') return null;
  const code = new URLSearchParams(location.search).get('error');
  history.replaceState(null, '', '/');
  if (!code) return null;
  const text = reasonText(lang, code);
  return text === code ? t(lang, 'loginFailed') : text;
}

/** Sign-out clears what the app keeps on this device (FR-2); the language choice stays. */
async function clearDeviceCaches(): Promise<void> {
  try {
    if ('caches' in window) for (const key of await caches.keys()) await caches.delete(key);
  } catch {
    // nothing cached yet (no service worker in A0)
  }
}

function SignedIn({
  auth,
  session,
  lang,
  pairToken,
  onSession,
  onSignedOut,
  onToggleLang,
}: {
  auth: AuthClient;
  session: AuthSessionResponse;
  lang: Lang;
  pairToken: string | null;
  onSession: (s: AuthSessionResponse) => void;
  onSignedOut: (reason?: string) => void;
  onToggleLang: () => void;
}) {
  const [machines, setMachines] = useState<MachineListItem[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTabState] = useState<TabId>(() => tabOfHash(location.hash));
  const [page, setPage] = useState<SubPage>(() => pageOfHash(location.hash));
  // where the app is and on which entry of the history (n), for the pattern of the next page change (nav-motion.ts):
  // opening a page goes forward, the way back goes back, the tab bar fades through. The depth of a page does not tell
  // it: a shot's page hangs under the tab, but opened from "Verlauf" the way back still leads back.
  const at = useRef<{ tab: TabId; page: SubPage; n: number }>({
    tab: tabOfHash(location.hash),
    page: pageOfHash(location.hash),
    n: (history.state as NavState | null)?.n ?? 0,
  });
  const go = useCallback(
    (to: { tab: TabId; page: SubPage }, kind: NavMotion | null, write: () => void, n = at.current.n) => {
      // the same place again (the active tab tapped) does not move
      const same = hashOfPage(to.tab, to.page) === hashOfPage(at.current.tab, at.current.page);
      at.current = { ...to, n };
      navigate(same ? null : kind, () => {
        setTabState(to.tab);
        setPage(to.page);
        write();
      });
    },
    [],
  );
  // the tab is in the address (#bezug …; the local app's #brew … still work), without filling the history
  const setTab = useCallback(
    (next: TabId) =>
      go({ tab: next, page: { page: 'main' } }, 'tab', () =>
        history.replaceState({ n: at.current.n } satisfies NavState, '', `#${next}`),
      ),
    [go],
  );
  // a sub-page (#bezug/rezept, #einstellungen/zeitplan …) is an entry in the history: the phone's back gesture leaves it;
  // the machine tab opens pages of the Bezug tab (recipe, a shot) the same way
  const openPage = useCallback(
    (next: TabId, p: SubPage) => {
      const n = at.current.n + 1;
      go(
        { tab: next, page: p },
        'forward',
        () => history.pushState({ brewcore: true, n } satisfies NavState, '', hashOfPage(next, p)),
        n,
      );
    },
    [go],
  );
  // a page that ends in another one (a new bean becomes its own page): the same entry of the history, so the way back
  // leads where the first one came from, not into the finished form
  const replacePage = useCallback(
    (next: TabId, p: SubPage) =>
      go({ tab: next, page: p }, 'forward', () =>
        history.replaceState({ brewcore: true, n: at.current.n } satisfies NavState, '', hashOfPage(next, p)),
      ),
    [go],
  );
  // Einstellungen → Claude from the Claude card and the setup line after a shot
  const openClaude = useCallback(() => openPage('einstellungen', { page: 'claude' }), [openPage]);
  const backPage = useCallback(() => {
    if ((history.state as NavState | null)?.brewcore) {
      history.back();
      return;
    }
    const here = tabOfHash(location.hash);
    const parent = parentOf(pageOfHash(location.hash));
    go({ tab: here, page: parent }, 'back', () =>
      history.replaceState({ n: at.current.n } satisfies NavState, '', hashOfPage(here, parent)),
    );
  }, [go]);
  useEffect(() => {
    // going back fires popstate and then hashchange for the same address: only the first one moves
    const onHash = (e: Event) => {
      const to = { tab: tabOfHash(location.hash), page: pageOfHash(location.hash) };
      if (hashOfPage(to.tab, to.page) === hashOfPage(at.current.tab, at.current.page)) return;
      const n = (history.state as NavState | null)?.n ?? 0;
      // back through the history goes back, the browser's forward button forward, an address typed in by its depth;
      // the browser's own back swipe animates itself
      const own = (e as PopStateEvent & { hasUAVisualTransition?: boolean }).hasUAVisualTransition === true;
      const kind = own
        ? null
        : e.type === 'popstate'
          ? n < at.current.n
            ? 'back'
            : 'forward'
          : navMotionOf(at.current, to);
      go(to, kind, () => undefined, n);
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onHash);
    };
  }, [go]);
  const [pairing, setPairing] = useState(pairToken !== null);
  const [accountOpen, setAccountOpen] = useState(false);
  const pairingApi = useMemo(() => new PairingApi(), []);
  const loadMachines = useCallback(async () => {
    const res = await fetch('/api/machines', { credentials: 'same-origin' });
    if (!res.ok) {
      setMachines((m) => m ?? []);
      return;
    }
    const parsed = validate('http-machines', await res.json());
    setMachines(parsed.ok ? parsed.value : []);
  }, []);
  const client = useMemo(() => {
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    return new BrewcoreClient({
      url: `${scheme}://${location.host}/ws`,
      factory: (url) => new WebSocket(url) as unknown as SocketLike,
      appVersion: APP_VERSION,
      isOnline: () => navigator.onLine,
    });
  }, []);
  const view = useSyncExternalStore(client.subscribe, client.getSnapshot);

  useEffect(() => {
    void loadMachines();
    client.start();
    return () => client.close();
  }, [client, loadMachines]);

  // the API closed the connection (4401/4403): a new login in this browser keeps going, otherwise back to the login
  useEffect(() => {
    if (view.auth !== 'lost') return;
    void auth.session().then((r) => {
      if (r.ok && r.value) {
        onSession(r.value);
        client.start();
      } else if (r.ok) onSignedOut(t(lang, 'sessionEnded'));
    });
  }, [view.auth, auth, client, lang, onSession, onSignedOut]);

  // back in the foreground: reconnect at once, renew the sliding session once an hour
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      client.reconnectNow();
      if (auth.dueForRefresh())
        void auth.session().then((r) => {
          if (r.ok && r.value) onSession(r.value);
          else if (r.ok) onSignedOut(t(lang, 'sessionEnded'));
        });
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [client, auth, lang, onSession, onSignedOut]);

  const list = machines ?? [];
  const machine = list.find((m) => m.id === selected) ?? list[0];
  const accountButton = (
    <AccountButton
      email={session.user.email}
      name={session.user.name}
      roleText={session.user.role === 'operator' ? t(lang, 'operator') : undefined}
      label={t(lang, 'accountOpen')}
      onClick={() => setAccountOpen(true)}
    />
  );
  const tabs = tabItems(lang);
  const switcher =
    list.length > 1 ? (
      <div className="machine-switch" role="group" aria-label={t(lang, 'machinesLabel')}>
        {list.map((m) => (
          <button
            key={m.id}
            type="button"
            className="machine-switch-item"
            aria-pressed={m.id === machine?.id}
            onClick={() => setSelected(m.id)}
          >
            {m.name}
          </button>
        ))}
      </div>
    ) : undefined;

  return (
    <div className={machine ? 'shell has-tabbar' : 'shell'}>
      <CloudBar client={client} lang={lang} />
      {machines === null && <LoadingScreen lang={lang} />}
      {machines !== null && !machine && (
        <FirstRun
          lang={lang}
          name={session.user.name}
          email={session.user.email}
          accountButton={accountButton}
          onPair={() => setPairing(true)}
        />
      )}
      {machine && (
        <>
          <MachineProvider key={machine.id} client={client} machineId={machine.id} lang={lang}>
            <DocumentTitle />
            <ClaudeSettingsLink value={openClaude}>
              <UntilLoaded lang={lang}>
                {tab === 'maschine' && (
                  <MachineHome
                    client={client}
                    machineId={machine.id}
                    lang={lang}
                    accountButton={accountButton}
                    switcher={switcher}
                    onOpenRecipe={() => openPage('bezug', { page: 'recipe' })}
                    onOpenShot={(id) => openPage('bezug', { page: 'shot', id })}
                    onOpenHistory={() => openPage('bezug', { page: 'history' })}
                    onOpenSchedule={() => openPage('einstellungen', { page: 'schedule' })}
                  />
                )}
                {tab === 'bezug' && (
                  <BrewTab
                    title={tabs[1]?.label ?? ''}
                    accountButton={accountButton}
                    page={page as BrewPage}
                    onPage={(p) => openPage('bezug', p)}
                    onReplace={(p) => replacePage('bezug', p)}
                    onBack={backPage}
                  />
                )}
                {tab === 'einstellungen' && (
                  <SettingsTab
                    title={tabs[2]?.label ?? ''}
                    accountButton={accountButton}
                    api={pairingApi}
                    onUnpaired={() => void loadMachines()}
                    page={page as SettingsPage}
                    onPage={(p) => openPage('einstellungen', p)}
                    onBack={backPage}
                  />
                )}
                {tab === 'wartung' && (
                  <CareTab
                    title={tabs[3]?.label ?? ''}
                    accountButton={accountButton}
                    page={page as CarePage}
                    onPage={(p) => openPage('wartung', p)}
                    onBack={backPage}
                  />
                )}
              </UntilLoaded>
            </ClaudeSettingsLink>
            <CommandToast />
          </MachineProvider>
          <TabBar tabs={tabs} active={tab} onChange={setTab} label={t(lang, 'tabsLabel')} />
        </>
      )}
      {pairing && (
        <PairingFlow
          lang={lang}
          api={pairingApi}
          initialToken={pairToken}
          onClose={() => {
            forgetPairingToken();
            setPairing(false);
          }}
          onPaired={(id) => {
            forgetPairingToken();
            setSelected(id);
            setTab('maschine');
            void loadMachines();
          }}
        />
      )}
      {accountOpen && (
        <AccountSheet
          auth={auth}
          session={session}
          lang={lang}
          net={view.net}
          onClose={() => setAccountOpen(false)}
          onSignedOut={() => onSignedOut()}
          onToggleLang={onToggleLang}
          onPair={
            machine
              ? () => {
                  setAccountOpen(false);
                  setPairing(true);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

/**
 * Connection of this phone to the cloud. Quiet while all is well (text for screen readers and tests only), a
 * bar at the top otherwise. `bar` false keeps it quiet (a gap that is still short, core/calm.ts calmNet).
 */
export function NetBar({ net, lang, bar = net !== 'ok' }: { net: NetState; lang: Lang; bar?: boolean }) {
  return (
    <p className={bar ? `netbar netbar-${net}` : 'visually-hidden'} role="status" aria-live="polite">
      {bar && (net === 'connecting' ? <Spinner size="sm" /> : <Icon name="cloudOff" size="sm" />)}
      <span data-testid="net-state">{t(lang, `net_${net}`)}</span>
    </p>
  );
}

/** The cloud bar of the signed-in app: a gap of this device shows only once it lasts (core/calm.ts). */
function CloudBar({ client, lang }: { client: BrewcoreClient; lang: Lang }) {
  const view = useSyncExternalStore(client.subscribe, client.getSnapshot);
  const now = useNow();
  const calm = calmNet(view.net, view.netSince, now);
  return <NetBar net={calm.net} bar={calm.bar} lang={lang} />;
}

export function App() {
  const [lang, setLang] = useState<Lang>(initialLang);
  const auth = useMemo(() => new AuthClient(), []);
  const [session, setSession] = useState<'loading' | null | AuthSessionResponse>('loading');
  const [loginError, setLoginError] = useState<string | null>(() => takeLoginError(lang));
  const [pairToken] = useState<string | null>(takePairingToken);

  useEffect(() => {
    void auth.session().then((r) => setSession(r.ok ? r.value : null));
  }, [auth]);

  const onSession = useCallback((s: AuthSessionResponse) => setSession(s), []);
  const onSignedOut = useCallback((reason?: string) => {
    // signing out (not an ended session) removes what the first Claude version left in this browser (its key now lives
    // in the account, src/ai/storage.ts)
    if (reason === undefined) forgetClaudeOnDevice();
    setSession(null);
    setLoginError(reason ?? null);
    void clearDeviceCaches();
  }, []);

  const toggleLang = useCallback(() => {
    const next: Lang = lang === 'de' ? 'en' : 'de';
    setLang(next);
    document.documentElement.lang = next;
    try {
      localStorage.setItem('brewcore.lang', next);
    } catch {
      // per-browser convenience only
    }
  }, [lang]);

  return (
    <div className="app">
      {session !== null && <h1 className="visually-hidden">{t(lang, 'title')}</h1>}
      {session === 'loading' && (
        <div className="splash" role="status">
          <BrandMark className="splash-mark" />
          <span className="visually-hidden">{t(lang, 'loading')}</span>
        </div>
      )}
      {session === null && (
        <LoginScreen
          auth={auth}
          lang={lang}
          initialError={loginError}
          onToggleLang={toggleLang}
          onSignedIn={(s) => {
            setLoginError(null);
            setSession(s);
          }}
        />
      )}
      {session !== null && session !== 'loading' && (
        <SignedIn
          key={session.user.id}
          auth={auth}
          session={session}
          lang={lang}
          pairToken={pairToken}
          onSession={onSession}
          onSignedOut={onSignedOut}
          onToggleLang={toggleLang}
        />
      )}
    </div>
  );
}

function LoadingScreen({ lang }: { lang: Lang }) {
  return (
    <div className="screen screen-loading" role="status">
      <Spinner />
      <span className="text-dim">{t(lang, 'loadingMachines')}</span>
    </div>
  );
}

/**
 * The tabs only once the machine's first snapshot is there: before, they drew placeholders (switches off, dashes,
 * "Nur der Besitzer kann das ändern") and then jumped to the real values (22.10.2026). A connection lost later keeps
 * the last values on screen (calm, core/calm.ts); this waits only for the very first data.
 */
function UntilLoaded({ lang, children }: { lang: Lang; children: ReactNode }) {
  const { ctx } = useMachine();
  if (ctx.view.info === null && !ctx.view.accessEnded) return <LoadingScreen lang={lang} />;
  return <>{children}</>;
}
